/**
 * RunPackingBoard — touch-first packing board for one delivery run.
 * Opened from the Runs list ("Pack" button on a run card).
 *
 *   - Stop tiles in delivery order with live pack progress
 *   - Tap a stop → modal with big steppers, Create bag, print delivery
 *     card, delete bag, Finish stop (complete-picking)
 *   - Stop tile turns green when fully packed & handed off; ROUTE COMPLETE
 *     banner when every stop is done.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Printer, Trash2, Plus, Minus,
  CheckCircle2, PackageOpen, X, Undo2,
} from "lucide-react";
import { openDeliveryCard } from "./DispatchPickingView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NC = { headers: { "x-no-cache": "1" } };

export default function RunPackingBoard({ routeId, onBack }) {
  const [route, setRoute] = useState(null);
  const [details, setDetails] = useState({}); // order_id → bundles response
  const [hotels, setHotels] = useState([]);
  const [products, setProducts] = useState([]);
  const [openStop, setOpenStop] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = (await axios.get(`${API}/delivery-routes/${routeId}`, NC)).data;
      setRoute(r);
      const orderStops = (r.stops || []).filter((s) => s.kind === "order" && !s.missing);
      const results = await Promise.all(
        orderStops.map((s) =>
          axios.get(`${API}/dispatch/orders/${s.order_id}/bundles`, NC)
            .then((res) => res.data).catch(() => null)
        )
      );
      const map = {};
      orderStops.forEach((s, i) => { if (results[i]) map[s.order_id] = results[i]; });
      setDetails(map);
    } catch (e) {
      toast.error("Failed to load run");
    }
  }, [routeId]);

  useEffect(() => {
    load();
    axios.get(`${API}/users`).then((res) => setHotels(res.data || [])).catch(() => {});
    // Products flagged is_default_extra are auto-included on every new bag
    // (typically laundry bags, reject bags, etc). We fetch the full product
    // catalog once here and filter downstream so the PackStopModal doesn't
    // have to re-fetch on every open.
    axios.get(`${API}/products`).then((res) => setProducts(res.data || [])).catch(() => {});
    const id = setInterval(load, 15000);
    // ESC anywhere on the board = go back to Runs. Only fires when no stop
    // modal is open (otherwise the modal owns the ESC key).
    const onKey = (e) => {
      if (e.key === "Escape" && !openStop) onBack?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [load, onBack, openStop]);

  const stopState = (detail) => {
    if (!detail) return { ordered: 0, packedPcs: 0, packingBags: 0, bags: 0, complete: false };
    const ordered = (detail.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
    const packedByPid = detail.packed_totals || {};
    const packedPcs = Object.values(packedByPid).reduce((s, v) => s + v, 0);
    const bundles = detail.bundles || [];
    const packingBags = bundles.filter((b) => b.status === "packing").length;
    const complete = ordered > 0 && packedPcs >= ordered && packingBags === 0 && bundles.length > 0;
    return { ordered, packedPcs, packingBags, bags: bundles.length, complete };
  };

  const orderStops = useMemo(
    () => (route?.stops || []).filter((s) => s.kind === "order" && !s.missing),
    [route]
  );
  const doneCount = orderStops.filter((s) => stopState(details[s.order_id]).complete).length;
  const allDone = orderStops.length > 0 && doneCount === orderStops.length;

  if (!route) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="packrun-board">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            data-testid="packrun-back"
            className="h-12 px-4 rounded-lg border-2 border-brand-navy bg-white text-brand-navy font-semibold flex items-center gap-2 hover:bg-brand-navy hover:text-white active:scale-95 transition shrink-0"
            aria-label="Back to Runs (Esc)"
            title="Back to Runs (Esc)"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Runs</span>
          </button>
          <div className="min-w-0">
            <h3 className="font-display text-xl font-bold text-brand-navy truncate">Packing — {route.name || "Run"}</h3>
            <div className="text-xs text-muted-foreground">
              {route.delivery_date}{route.driver ? ` · ${route.driver}` : ""}{route.vehicle ? ` · ${route.vehicle}` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="font-mono tabular-nums text-2xl font-bold text-brand-navy" data-testid="packrun-progress">
              {doneCount}<span className="text-neutral-400">/{orderStops.length}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">stops packed</div>
          </div>
          <button
            onClick={load}
            data-testid="packrun-refresh"
            className="h-12 w-12 rounded-lg border border-neutral-300 bg-white flex items-center justify-center hover:border-brand-navy active:scale-95 transition"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button
            onClick={onBack}
            data-testid="packrun-close"
            className="h-12 px-4 rounded-lg bg-brand-navy hover:bg-brand-navy-hover text-white font-semibold flex items-center gap-2 active:scale-95 transition"
            title="Close (Esc)"
          >
            <X className="h-5 w-5" />
            <span>Close</span>
          </button>
        </div>
      </div>

      {allDone && (
        <div className="rounded-xl bg-emerald-600 text-white px-5 py-4 flex items-center gap-3" data-testid="packrun-complete">
          <CheckCircle2 className="h-8 w-8" />
          <div>
            <div className="font-display text-lg font-bold">PACKING COMPLETE</div>
            <div className="text-emerald-100 text-sm">Every stop on this run is fully packed. Head back to Runs and open Delivery arrangement to prepare the truck.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orderStops.map((s, idx) => {
          const detail = details[s.order_id];
          const st = stopState(detail);
          const started = st.bags > 0 && !st.complete;
          return (
            <button
              key={s.order_id}
              data-testid={`packrun-stop-${s.order_id}`}
              onClick={() => setOpenStop(s.order_id)}
              className={`text-left rounded-xl p-4 border-2 transition active:scale-[0.99] ${
                st.complete
                  ? "bg-emerald-50 border-emerald-400"
                  : started
                    ? "bg-amber-50 border-amber-400"
                    : "bg-white border-neutral-200 hover:border-brand-navy"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Stop {idx + 1}</div>
                  <div className="font-display text-lg font-bold text-brand-navy truncate mt-0.5">{s.hotel_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{s.order_number}</div>
                </div>
                {st.complete ? (
                  <CheckCircle2 className="h-8 w-8 text-emerald-500 shrink-0" />
                ) : (
                  <div className="text-right shrink-0">
                    <div className="font-mono tabular-nums text-xl font-bold text-brand-navy">
                      {st.packedPcs}<span className="text-neutral-400">/{st.ordered}</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">pcs packed</div>
                  </div>
                )}
              </div>
              <div className="mt-2 space-y-0.5">
                {(detail?.items || []).slice(0, 4).map((it) => {
                  const packed = (detail?.packed_totals || {})[it.product_id] || 0;
                  const full = packed >= (it.quantity || 0);
                  return (
                    <div key={it.product_id} className="flex items-center justify-between text-xs">
                      <span className="truncate text-neutral-600">{it.product_name}</span>
                      <span className={`font-mono tabular-nums font-bold ${full ? "text-emerald-600" : "text-neutral-800"}`}>
                        {packed}/{it.quantity}
                      </span>
                    </div>
                  );
                })}
                {(detail?.items || []).length > 4 && (
                  <div className="text-[11px] text-muted-foreground">+{detail.items.length - 4} more…</div>
                )}
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                {st.bags} bag{st.bags === 1 ? "" : "s"}{st.packingBags > 0 ? ` · ${st.packingBags} still packing` : ""}
                {st.complete ? " · DONE" : ""}
              </div>
            </button>
          );
        })}
      </div>

      {openStop && details[openStop] && (
        <PackStopModal
          initialDetail={details[openStop]}
          hotels={hotels}
          products={products}
          onClose={() => { setOpenStop(null); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────── Pack modal (touch-first) ───────────────────────
// Owns its own copy of the order detail and refetches it directly after every
// mutation — rapid Create bag → Finish stop taps always act on fresh state.
export function PackStopModal({ initialDetail, hotels, products, onClose }) {
  const [detail, setDetail] = useState(initialDetail);
  const [qty, setQty] = useState({});
  const [extras, setExtras] = useState([]); // [{product_id, product_name, quantity}]
  const [busy, setBusy] = useState(false);
  const initialised = useRef(false);

  // ESC key = close the modal (matches OS convention; users kept getting
  // "stuck" inside the pack picker with only a tiny X in the corner).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const refetch = async () => {
    const res = await axios.get(`${API}/dispatch/orders/${initialDetail.order_id}/bundles`, NC);
    setDetail(res.data);
  };

  const orderLines = detail.items || [];
  const packedByPid = detail.packed_totals || {};
  const bundles = detail.bundles || [];
  const packingCount = bundles.filter((b) => b.status === "packing").length;

  // Fast lookup product_id → product record so we can read each product's
  // per-item minimum_quantity (drives the ±N bulk step button below).
  const productsById = useMemo(() => {
    const m = new Map();
    for (const p of (products || [])) if (p && p.id) m.set(p.id, p);
    return m;
  }, [products]);
  const stepFor = (pid) => {
    const p = productsById.get(pid);
    const n = p?.minimum_quantity;
    return Number.isFinite(n) && n > 0 ? n : 10;
  };

  // Products flagged is_default_extra are the "always included" freebies
  // (typically Laundry Bag, Reject Bag) auto-appended to every new bag.
  // The operator can bump qty, drop it to 0, or click Remove to skip.
  const defaultExtras = useMemo(
    () => (products || []).filter((p) => p && p.is_default_extra),
    [products],
  );

  const remaining = useMemo(() => {
    const out = {};
    orderLines.forEach((l) => {
      out[l.product_id] = Math.max(0, (l.quantity || 0) - (packedByPid[l.product_id] || 0));
    });
    return out;
  }, [orderLines, packedByPid]);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    // Order lines: default qty = 0 (operator explicitly enters how many go
    // into THIS bag — no more "pack it all at once" surprises).
    const init = {};
    orderLines.forEach((l) => { init[l.product_id] = 0; });
    setQty(init);
    // Auto-append default extras with qty 1 each. The operator can Remove
    // them if this particular bag doesn't need e.g. a laundry bag.
    setExtras(defaultExtras.map((p) => ({
      product_id: p.id,
      product_name: p.name,
      quantity: 1,
    })));
  }, [orderLines, remaining, defaultExtras]);

  // Order-line quantity mutator — caps at remaining (never over-pack).
  const bump = (pid, delta) => {
    setQty((prev) => {
      const cap = remaining[pid] || 0;
      const next = Math.min(cap, Math.max(0, (prev[pid] || 0) + delta));
      return { ...prev, [pid]: next };
    });
  };
  const setQtyDirect = (pid, raw) => {
    setQty((prev) => {
      const cap = remaining[pid] || 0;
      const n = parseInt(raw, 10);
      const clean = Number.isFinite(n) ? Math.min(cap, Math.max(0, n)) : 0;
      return { ...prev, [pid]: clean };
    });
  };
  // Extras quantity mutator — NO cap (extras aren't part of the order).
  const bumpExtra = (pid, delta) => {
    setExtras((prev) => prev.map((r) => r.product_id === pid
      ? { ...r, quantity: Math.max(0, (r.quantity || 0) + delta) }
      : r));
  };
  const setExtraDirect = (pid, raw) => {
    setExtras((prev) => prev.map((r) => {
      if (r.product_id !== pid) return r;
      const n = parseInt(raw, 10);
      return { ...r, quantity: Number.isFinite(n) ? Math.max(0, n) : 0 };
    }));
  };
  const removeExtra = (pid) => {
    setExtras((prev) => prev.filter((r) => r.product_id !== pid));
  };

  const bagPcs =
    Object.values(qty).reduce((s, v) => s + (v || 0), 0) +
    extras.reduce((s, r) => s + (r.quantity || 0), 0);

  const addBag = async () => {
    if (busy || bagPcs === 0) return;
    setBusy(true);
    try {
      const items = [
        ...orderLines
          .filter((l) => (qty[l.product_id] || 0) > 0)
          .map((l) => ({
            product_id: l.product_id, product_name: l.product_name,
            quantity: qty[l.product_id],
          })),
        ...extras
          .filter((r) => (r.quantity || 0) > 0)
          .map((r) => ({
            product_id: r.product_id, product_name: r.product_name,
            quantity: r.quantity, is_extra: true,
          })),
      ];
      const res = await axios.post(`${API}/dispatch/orders/${detail.order_id}/bundles`, { items });
      toast.success(`Bag #${res.data.bundle_no} created`);
      initialised.current = false;
      // Auto-finish: if this bag completed the whole order, flip the packs
      // to "packed" automatically — no manual "Finish pack" tap needed.
      const fresh = (await axios.get(`${API}/dispatch/orders/${detail.order_id}/bundles`, NC)).data;
      setDetail(fresh);
      const packedNow = fresh.packed_totals || {};
      const lines = fresh.items || [];
      const allPacked = lines.length > 0 && lines.every((l) => (packedNow[l.product_id] || 0) >= (l.quantity || 0));
      if (allPacked) {
        const fin = await axios.post(`${API}/dispatch/orders/${detail.order_id}/complete-picking`);
        toast.success(`All items packed — pack finished automatically (${fin.data?.flipped || 0} bag(s))`);
        onClose();
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create bag");
    } finally {
      setBusy(false);
    }
  };

  const deleteBag = async (b) => {
    try {
      await axios.delete(`${API}/dispatch/orders/${detail.order_id}/bundles/${b.id}`);
      toast.success(`Bag #${b.bundle_no} removed`);
      initialised.current = false;
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove bag");
    }
  };

  // Reopen a packed bag back to `packing` so the operator can edit
  // contents or delete it. Only exposed for the `packed` status; once a
  // bag is dispatched/delivered it's out of the packing operator's hands.
  const reopenBag = async (b) => {
    try {
      await axios.post(`${API}/dispatch/orders/${detail.order_id}/bundles/${b.id}/reopen-picking`);
      toast.success(`Bag #${b.bundle_no} reopened — you can now edit or delete it`);
      initialised.current = false;
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reopen bag");
    }
  };

  const printCard = (b) => {
    const hotelObj = hotels.find((h) => h.id === detail.hotel_id) || null;
    openDeliveryCard(b, detail, hotelObj, orderLines);
  };

  const finishStop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/dispatch/orders/${detail.order_id}/complete-picking`);
      toast.success(`Pack complete — ${res.data?.flipped || 0} bag(s) ready for delivery`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete picking");
    } finally {
      setBusy(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
      data-testid="packrun-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white w-full max-w-5xl rounded-xl border border-neutral-200 shadow-xl flex flex-col max-h-[calc(100vh-2rem)] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-neutral-50 shrink-0">
          <div className="min-w-0">
            <div className="font-display text-lg font-bold text-brand-navy truncate">{detail.hotel_name}</div>
            <div className="font-mono text-[11px] text-muted-foreground">{detail.order_number} · delivery {detail.delivery_date}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="packrun-modal-close"
            className="h-12 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold flex items-center gap-2 active:scale-95 transition shrink-0"
            title="Close (Esc)"
            aria-label="Close (Esc)"
          >
            <X className="h-5 w-5" />
            <span>Close</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-0 md:divide-x">
          {/* LEFT — bag builder */}
          <div className="p-4 space-y-2.5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">
              New bag — tap ±N (per-product step) or ±1, or type directly
            </div>
            {orderLines.map((l) => {
              const packed = packedByPid[l.product_id] || 0;
              const rem = remaining[l.product_id] || 0;
              const cur = qty[l.product_id] || 0;
              const step = stepFor(l.product_id);
              return (
                <div key={l.product_id} className="rounded-lg border bg-neutral-50 px-3 py-2.5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{l.product_name}</div>
                      <div className="text-xs text-muted-foreground">
                        packed <span className={packed >= l.quantity ? "text-emerald-600 font-bold" : ""}>{packed}/{l.quantity}</span>
                        {rem > 0 ? ` · ${rem} left` : " · done"}
                        <span className="ml-1 text-neutral-400">· step {step}</span>
                      </div>
                    </div>
                  </div>
                  <QtySteppers
                    testIdPrefix={`packrun-line-${l.product_id}`}
                    value={cur}
                    max={rem}
                    step={step}
                    onBump={(d) => bump(l.product_id, d)}
                    onSet={(v) => setQtyDirect(l.product_id, v)}
                  />
                </div>
              );
            })}

            {/* Extras (auto-included default-extra products) */}
            {extras.length > 0 && (
              <div className="pt-2 mt-2 border-t space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">
                  Extras (auto-included · removable)
                </div>
                {extras.map((r) => {
                  const step = stepFor(r.product_id);
                  return (
                    <div key={r.product_id} className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2.5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{r.product_name}</div>
                          <div className="text-[10px] text-muted-foreground">Extra — not counted against ordered pcs · step {step}</div>
                        </div>
                        <button
                          onClick={() => removeExtra(r.product_id)}
                          data-testid={`packrun-extra-remove-${r.product_id}`}
                          className="h-8 px-2 text-xs rounded border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 active:scale-95 transition"
                        >
                          Remove
                        </button>
                      </div>
                      <QtySteppers
                        testIdPrefix={`packrun-extra-${r.product_id}`}
                        value={r.quantity || 0}
                        max={9999}
                        step={step}
                        onBump={(d) => bumpExtra(r.product_id, d)}
                        onSet={(v) => setExtraDirect(r.product_id, v)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={addBag}
              disabled={busy || bagPcs === 0}
              data-testid="packrun-add-bag"
              className="w-full h-14 rounded-lg bg-brand-navy hover:bg-brand-navy-hover text-white font-display font-bold text-lg active:scale-[0.99] transition disabled:opacity-30 flex items-center justify-center gap-2"
            >
              <PackageOpen className="h-5 w-5" /> {busy ? "Working…" : `Create bag (${bagPcs} pcs)`}
            </button>
          </div>

          {/* RIGHT — bags + finish */}
          <div className="p-4 space-y-2.5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Bags ({bundles.length})</div>
            {bundles.length === 0 && <p className="text-sm text-muted-foreground">No bags yet.</p>}
            {bundles.map((b) => (
              <div key={b.id} className="rounded-lg border bg-white px-3 py-2.5" data-testid={`packrun-bag-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm">
                      Bag #{b.bundle_no}
                      <span className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        b.status === "packing"
                          ? "bg-neutral-100 text-neutral-600 border-neutral-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {b.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {(b.items || []).map((it) => `${it.product_name} ×${it.quantity}`).join(", ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => printCard(b)}
                      data-testid={`packrun-print-${b.id}`}
                      className="h-11 w-11 rounded-lg border bg-white flex items-center justify-center hover:border-brand-navy active:scale-95 transition"
                      title="Print delivery card"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                    {b.status === "packed" && (
                      <button
                        onClick={() => reopenBag(b)}
                        data-testid={`packrun-reopen-${b.id}`}
                        className="h-11 px-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-1 active:scale-95 transition text-xs font-semibold"
                        title="Reopen this bag so you can edit or delete it"
                      >
                        <Undo2 className="h-4 w-4" />
                        <span>Reopen</span>
                      </button>
                    )}
                    {b.status === "packing" && (
                      <button
                        onClick={() => deleteBag(b)}
                        data-testid={`packrun-delete-${b.id}`}
                        className="h-11 w-11 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 flex items-center justify-center active:scale-95 transition"
                        title="Delete this bag"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={finishStop}
              disabled={busy || packingCount === 0}
              data-testid="packrun-finish-stop"
              className="w-full h-14 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-display font-bold text-lg active:scale-[0.99] transition disabled:opacity-30 flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="h-5 w-5" /> {busy ? "Working…" : `Finish pack (${packingCount} bag${packingCount === 1 ? "" : "s"})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}


// ────── Big-touch quantity control: −N −1 [input] +1 +N ──────
// `step` = per-product minimum_quantity (e.g. sheets 10, pillow cases 50).
// Falls back to 10. Same row is reused for order lines (capped at `max`)
// and extras (max=9999).
function QtySteppers({ testIdPrefix, value, max, step = 10, onBump, onSet }) {
  const disableMinus = value <= 0;
  const disablePlus = value >= max;
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onBump(-step)}
        disabled={disableMinus}
        data-testid={`${testIdPrefix}-minus-${step}`}
        className="h-12 min-w-[3rem] px-2 rounded-lg border-2 border-rose-200 bg-rose-50 text-rose-700 flex items-center justify-center font-mono font-bold text-sm active:scale-95 transition disabled:opacity-30"
      >
        −{step}
      </button>
      <button
        onClick={() => onBump(-1)}
        disabled={disableMinus}
        data-testid={`${testIdPrefix}-minus-1`}
        className="h-12 w-12 rounded-lg border bg-white flex items-center justify-center active:scale-95 transition disabled:opacity-30"
      >
        <Minus className="h-5 w-5" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onSet(e.target.value.replace(/[^0-9]/g, ""))}
        data-testid={`${testIdPrefix}-input`}
        className="h-12 w-16 rounded-lg border-2 border-brand-navy/40 bg-white text-center font-mono tabular-nums font-bold text-xl focus:outline-none focus:border-brand-navy"
      />
      <button
        onClick={() => onBump(1)}
        disabled={disablePlus}
        data-testid={`${testIdPrefix}-plus-1`}
        className="h-12 w-12 rounded-lg border bg-white flex items-center justify-center active:scale-95 transition disabled:opacity-30"
      >
        <Plus className="h-5 w-5" />
      </button>
      <button
        onClick={() => onBump(step)}
        disabled={disablePlus}
        data-testid={`${testIdPrefix}-plus-${step}`}
        className="h-12 min-w-[3rem] px-2 rounded-lg border-2 border-emerald-200 bg-emerald-50 text-emerald-700 flex items-center justify-center font-mono font-bold text-sm active:scale-95 transition disabled:opacity-30"
      >
        +{step}
      </button>
    </div>
  );
}
